import type {
  AllocationCombinationHotspot,
  AllocationCombinationPair,
  AllocationCombinationUser,
  RoleAllocation,
  StatusBreakdown,
  UserAllocationCombinationsResponse,
} from "../../../types/userAllocations";
import type { DealsActiveFilter } from "../../userAllocations/userAllocationsApi";

export type CombinationSelection = {
  leadUserId?: number;
  leadUnassigned?: boolean;
  supportUserId?: number;
  supportUnassigned?: boolean;
  leadName: string;
  supportName: string;
  byStatus: StatusBreakdown[];
};

type RawUserRef =
  | {
      user_id?: number | null;
      id?: number | null;
      name?: string | null;
    }
  | null
  | undefined;

type RawCombinationPair = Partial<AllocationCombinationPair> & {
  deal_lead?: RawUserRef;
  deal_support?: RawUserRef;
};

const EMPTY_ROLE_ALLOCATION: RoleAllocation = {
  deal_count: 0,
  loan_amount_gbp: 0,
  by_status: [],
};

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? value : [];
}

function userIdFromRef(ref: RawUserRef): number | null {
  if (ref == null) return null;
  const id = ref.user_id ?? ref.id;
  return id == null ? null : id;
}

function nameFromRef(ref: RawUserRef): string | null {
  if (ref == null) return null;
  return ref.name ?? null;
}

export function normalizeCombinationPair(
  raw: RawCombinationPair,
): AllocationCombinationPair {
  const leadRef = raw.deal_lead;
  const supportRef = raw.deal_support;

  return {
    lead_user_id:
      raw.lead_user_id !== undefined
        ? raw.lead_user_id
        : userIdFromRef(leadRef),
    lead_name:
      raw.lead_name !== undefined ? raw.lead_name : nameFromRef(leadRef),
    support_user_id:
      raw.support_user_id !== undefined
        ? raw.support_user_id
        : userIdFromRef(supportRef),
    support_name:
      raw.support_name !== undefined
        ? raw.support_name
        : nameFromRef(supportRef),
    deal_count: raw.deal_count ?? 0,
    loan_amount_gbp: raw.loan_amount_gbp ?? 0,
    by_status: asArray<StatusBreakdown>(raw.by_status),
    share_of_lead_deals: raw.share_of_lead_deals ?? 0,
    share_of_support_deals: raw.share_of_support_deals ?? 0,
  };
}

function normalizeRoleAllocation(raw: unknown): RoleAllocation {
  if (!raw || typeof raw !== "object") return EMPTY_ROLE_ALLOCATION;
  const value = raw as Partial<RoleAllocation>;
  return {
    deal_count: value.deal_count ?? 0,
    loan_amount_gbp: value.loan_amount_gbp ?? 0,
    by_status: asArray<StatusBreakdown>(value.by_status),
  };
}

function normalizeUsers(raw: unknown): UserAllocationCombinationsResponse["users"] {
  const value = (raw && typeof raw === "object" ? raw : {}) as Record<
    string,
    unknown
  >;

  const lead = asArray<AllocationCombinationUser>(
    value.deal_lead ?? value.leads ?? value.lead_users,
  );
  const support = asArray<AllocationCombinationUser>(
    value.deal_support ?? value.supports ?? value.support_users,
  );

  return {
    deal_lead: lead.map((user) => ({
      user_id: user.user_id ?? (user as { id?: number }).id!,
      name: user.name,
    })),
    deal_support: support.map((user) => ({
      user_id: user.user_id ?? (user as { id?: number }).id!,
      name: user.name,
    })),
  };
}

function extractRawPairs(raw: Record<string, unknown>): RawCombinationPair[] {
  const candidate =
    raw.pairs ?? raw.combinations ?? raw.allocation_combinations;
  return asArray<RawCombinationPair>(candidate);
}

/** Normalize API payload to the frontend combinations response shape. */
export function normalizeUserAllocationCombinationsResponse(
  raw: unknown,
): UserAllocationCombinationsResponse {
  const value = (raw && typeof raw === "object" ? raw : {}) as Record<
    string,
    unknown
  >;

  const pairs = extractRawPairs(value).map(normalizeCombinationPair);
  const hotspots = asArray<RawCombinationPair>(value.hotspots).map(
    (item) => normalizeCombinationPair(item) as AllocationCombinationHotspot,
  );

  let users = normalizeUsers(value.users);
  if (users.deal_lead.length === 0 || users.deal_support.length === 0) {
    users = deriveUsersFromPairs(pairs, users);
  }

  const unassignedRaw =
    value.unassigned && typeof value.unassigned === "object"
      ? (value.unassigned as Record<string, unknown>)
      : {};

  const totalRaw =
    value.total && typeof value.total === "object"
      ? (value.total as Record<string, unknown>)
      : {};

  return {
    pairs,
    users,
    hotspots,
    unassigned: {
      missing_lead: normalizeRoleAllocation(unassignedRaw.missing_lead),
      missing_support: normalizeRoleAllocation(unassignedRaw.missing_support),
      missing_both: normalizeRoleAllocation(unassignedRaw.missing_both),
    },
    total: {
      deal_count: Number(totalRaw.deal_count ?? 0),
      loan_amount_gbp: Number(totalRaw.loan_amount_gbp ?? 0),
    },
    fx_rates:
      value.fx_rates && typeof value.fx_rates === "object"
        ? (value.fx_rates as Record<string, number>)
        : {},
    deals_active_filter:
      typeof value.deals_active_filter === "boolean"
        ? value.deals_active_filter
        : undefined,
    status_ids_filter: asArray<number>(value.status_ids_filter),
  };
}

function deriveUsersFromPairs(
  pairs: AllocationCombinationPair[],
  existing: UserAllocationCombinationsResponse["users"],
): UserAllocationCombinationsResponse["users"] {
  const leadMap = new Map<number, string>();
  const supportMap = new Map<number, string>();

  for (const user of existing.deal_lead) {
    leadMap.set(user.user_id, user.name);
  }
  for (const user of existing.deal_support) {
    supportMap.set(user.user_id, user.name);
  }

  for (const pair of pairs) {
    if (pair.lead_user_id != null) {
      leadMap.set(pair.lead_user_id, pair.lead_name ?? `User ${pair.lead_user_id}`);
    }
    if (pair.support_user_id != null) {
      supportMap.set(
        pair.support_user_id,
        pair.support_name ?? `User ${pair.support_user_id}`,
      );
    }
  }

  return {
    deal_lead: [...leadMap.entries()]
      .map(([user_id, name]) => ({ user_id, name }))
      .sort((a, b) => a.name.localeCompare(b.name)),
    deal_support: [...supportMap.entries()]
      .map(([user_id, name]) => ({ user_id, name }))
      .sort((a, b) => a.name.localeCompare(b.name)),
  };
}

export function pairKey(
  leadUserId: number | null | undefined,
  supportUserId: number | null | undefined,
): string {
  const lead = leadUserId == null ? "u" : String(leadUserId);
  const support = supportUserId == null ? "u" : String(supportUserId);
  return `${lead}:${support}`;
}

export function combinationPairRowKey(pair: AllocationCombinationPair): string {
  return pairKey(pair.lead_user_id, pair.support_user_id);
}

export function buildPairLookup(
  pairs: AllocationCombinationPair[] | null | undefined,
): Map<string, AllocationCombinationPair> {
  const map = new Map<string, AllocationCombinationPair>();
  for (const pair of asArray<AllocationCombinationPair>(pairs)) {
    map.set(pairKey(pair.lead_user_id, pair.support_user_id), pair);
  }
  return map;
}

export function formatShare(value: number): string {
  const pct = value <= 1 ? value * 100 : value;
  return `${Math.round(pct)}%`;
}

export function formatGbpMm(value: number): string {
  const mm = value / 1_000_000;
  return `£${mm.toLocaleString("en-GB", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}`;
}

export function formatCount(value: number): string {
  return value.toLocaleString("en-GB");
}

/** Brand teal used for heatmap intensity (matches unassigned/support accents). */
const HEATMAP_RGB = { r: 27, g: 122, b: 99 };

export function heatmapCellBackground(
  value: number,
  maxValue: number,
): string {
  if (value <= 0 || maxValue <= 0) {
    return "transparent";
  }
  const ratio = Math.min(1, value / maxValue);
  const alpha = 0.08 + ratio * 0.72;
  return `rgba(${HEATMAP_RGB.r}, ${HEATMAP_RGB.g}, ${HEATMAP_RGB.b}, ${alpha.toFixed(3)})`;
}

export function combinationDisplayName(
  name: string | null | undefined,
  side: "lead" | "support",
): string {
  if (name) return name;
  return side === "lead" ? "Unassigned lead" : "Unassigned support";
}

export function selectionFromPair(
  pair: AllocationCombinationPair,
): CombinationSelection {
  const leadUnassigned = pair.lead_user_id == null;
  const supportUnassigned = pair.support_user_id == null;
  return {
    ...(leadUnassigned
      ? { leadUnassigned: true }
      : { leadUserId: pair.lead_user_id! }),
    ...(supportUnassigned
      ? { supportUnassigned: true }
      : { supportUserId: pair.support_user_id! }),
    leadName: combinationDisplayName(pair.lead_name, "lead"),
    supportName: combinationDisplayName(pair.support_name, "support"),
    byStatus: pair.by_status ?? [],
  };
}

export function buildCombinationDealsCacheKey(args: {
  leadUserId?: number;
  leadUnassigned?: boolean;
  supportUserId?: number;
  supportUnassigned?: boolean;
  statusRow: StatusBreakdown;
  dealsActive: DealsActiveFilter;
  statusIds?: number[];
}): string {
  const lead = args.leadUnassigned
    ? "u"
    : args.leadUserId != null
      ? String(args.leadUserId)
      : "?";
  const support = args.supportUnassigned
    ? "u"
    : args.supportUserId != null
      ? String(args.supportUserId)
      : "?";
  const status = args.statusRow.id ?? "null";
  const statusFilterKey =
    args.statusIds?.slice().sort((a, b) => a - b).join(",") ?? "";
  return `${lead}|${support}|${status}|${args.dealsActive}|${statusFilterKey}`;
}

export function collectCombinationStatuses(
  pairs: AllocationCombinationPair[],
): StatusBreakdown[] {
  const map = new Map<string, StatusBreakdown>();
  for (const pair of pairs) {
    for (const status of pair.by_status ?? []) {
      const key = `${status.id ?? "null"}-${status.sort_order}-${status.name}`;
      if (!map.has(key)) {
        map.set(key, status);
      }
    }
  }
  return [...map.values()].sort((a, b) => a.sort_order - b.sort_order);
}

export type CombinationStatusRow = {
  lead_user_id: number | null;
  lead_name: string | null;
  support_user_id: number | null;
  support_name: string | null;
  status: StatusBreakdown;
  pair: AllocationCombinationPair;
};

export function statusBreakdownKey(status: StatusBreakdown): string {
  return status.id == null
    ? `null:${status.sort_order}:${status.name}`
    : String(status.id);
}

export function flattenPairsByStatus(
  pairs: AllocationCombinationPair[],
  statusFilterKey?: string,
): CombinationStatusRow[] {
  const rows: CombinationStatusRow[] = [];

  for (const pair of pairs) {
    for (const status of pair.by_status ?? []) {
      if (status.deal_count <= 0) continue;
      if (
        statusFilterKey != null &&
        statusBreakdownKey(status) !== statusFilterKey
      ) {
        continue;
      }
      rows.push({
        lead_user_id: pair.lead_user_id,
        lead_name: pair.lead_name,
        support_user_id: pair.support_user_id,
        support_name: pair.support_name,
        status,
        pair,
      });
    }
  }

  return rows.sort((a, b) => {
    const order = a.status.sort_order - b.status.sort_order;
    if (order !== 0) return order;
    return b.status.deal_count - a.status.deal_count;
  });
}

export function selectionFromStatusRow(
  row: CombinationStatusRow,
): CombinationSelection {
  const base = selectionFromPair(row.pair);
  return {
    ...base,
    byStatus: [row.status],
  };
}

export function hotspotReasonLabel(reason?: string): string | null {
  if (!reason) return null;
  if (reason === "high_mutual_share") return "High mutual share";
  if (reason === "top_deal_count") return "Top by deals";
  return reason.replace(/_/g, " ");
}
